import type { TreeNode, TreePatch, UndoRecord, VersionedValue } from "./Types.ts";

// ─── UndoStack ────────────────────────────────────────────────────────────────
//
// A pure stack-based undo/redo history. No reference to DomTree — it only
// stores pre-computed UndoRecord objects provided by DomSession.
//
// DomSession reads the tree state BEFORE applying a patch and constructs the
// inverse patches, then calls UndoStack.push(). This keeps the timing concern
// (must read before mutating) in DomSession, not here.
//
// Deep-cloning "add" forward patches at push time ensures the stored node
// subtrees are isolated from future mutations to the live tree. Without this,
// a redo after editing a node's attribute would redo the edited state, not
// the original one.

export class UndoStack {
    public readonly undoStack: UndoRecord[];
    public readonly redoStack: UndoRecord[];
    public readonly maxSize: number;

    constructor(maxSize: number = 100) {
        this.undoStack = [];
        this.redoStack = [];
        this.maxSize = maxSize;
    }

    // ─── Stack inspection ─────────────────────────────────────────────────────

    public canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    public canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    public getUndoDepth(): number {
        return this.undoStack.length;
    }

    public getRedoDepth(): number {
        return this.redoStack.length;
    }
    public getLastUndoRecord(): UndoRecord | undefined {
        return this.undoStack[this.undoStack.length - 1];
    }
    public getLastRedoRecord(): UndoRecord | undefined {
        return this.redoStack[this.redoStack.length - 1];
    }
    // ─── Mutations ────────────────────────────────────────────────────────────

    /**
     * Pushes a new record onto the undo stack.
     * Deep-clones the node in any "add" forward patches to isolate them from
     * future live-tree mutations.
     * Clears the redo stack — any new action invalidates pending redo history.
     * Drops the oldest entry if the stack exceeds maxSize.
     */
    public push(record: UndoRecord): void {
        const safeRecord: UndoRecord = {
            clientIds: [...record.clientIds],
            forwardPatches: record.forwardPatches.map(patch => {
                if (patch.type === "add" && patch.node) {
                    return { ...patch, node: this.deepCloneNode(patch.node) };
                }
                return patch;
            }),
            inversePatches: record.inversePatches.map(patch => {
                if (patch.type === "add" && patch.node) {
                    return { ...patch, node: this.deepCloneNode(patch.node) };
                }
                return patch;
            }),
        };

        this.undoStack.push(safeRecord);
        this.redoStack.length = 0;

        if (this.undoStack.length > this.maxSize) {
            this.undoStack.shift();
        }
    }

    /**
     * Pops the top record from the undo stack and moves it to the redo stack.
     * Returns the record so DomSession can apply the inverse patches.
     * Returns null if the stack is empty.
     */
    public undo(): UndoRecord | null {
        if (this.undoStack.length === 0) {
            return null;
        }
        const record = this.undoStack.pop()!;
        this.redoStack.push(record);
        return record;
    }

    /** Returns the top undo record without removing it. */
    public peekUndo(): UndoRecord | undefined {
        return this.undoStack[this.undoStack.length - 1];
    }

    /** Removes the top undo record without pushing it to the redo stack. */
    public discardTopUndo(): void {
        this.undoStack.pop();
    }

    /**
     * Pops the top record from the redo stack and moves it to the undo stack.
     * Returns the record so DomSession can apply the forward patches.
     * Returns null if the redo stack is empty.
     */
    public redo(): UndoRecord | null {
        if (this.redoStack.length === 0) {
            return null;
        }
        const record = this.redoStack.pop()!;
        this.undoStack.push(record);
        return record;
    }

    /**
     * Removes the first record in either stack whose clientIds include the given id.
     * Called when the server rejects a patch — the action was never committed and
     * must not remain in the history.
     */
    public removeByClientId(clientId: string): void {
        const matches = (r: UndoRecord) =>
            r.clientIds.includes(clientId) || (r.pendingClientIds?.includes(clientId) ?? false);
        const undoIndex = this.undoStack.findIndex(matches);
        if (undoIndex !== -1) {
            this.undoStack.splice(undoIndex, 1);
            return;
        }
        const redoIndex = this.redoStack.findIndex(matches);
        if (redoIndex !== -1) {
            this.redoStack.splice(redoIndex, 1);
        }
    }

    /**
     * Returns the record that contains the given clientId without removing it.
     * Used by DomSession.onPatchRejected to retrieve the inverse patches before
     * removing the record.
     */
    public getRecordByClientId(clientId: string): UndoRecord | null {
        const matches = (r: UndoRecord) =>
            r.clientIds.includes(clientId) || (r.pendingClientIds?.includes(clientId) ?? false);
        const undoMatch = this.undoStack.find(matches);
        if (undoMatch) return undoMatch;
        return this.redoStack.find(matches) ?? null;
    }

    /**
     * Clears both stacks.
     * Called when a snapshot is loaded — the old history is invalid against the new tree.
     */
    public clear(): void {
        this.undoStack.length = 0;
        this.redoStack.length = 0;
    }

    // ─── Deep clone ───────────────────────────────────────────────────────────

    /**
     * Deep-clones a TreeNode and its entire subtree, including all Map fields.
     * Used to isolate the undo stack from future live-tree mutations.
     */
    private deepCloneNode(node: TreeNode): TreeNode {
        return {
            id: node.id,
            type: node.type,
            parentId: node.parentId,
            position: node.position,
            tagName: { ...node.tagName },
            text: { ...node.text },
            comment: { ...node.comment },
            attributes: this.cloneVersionedMap(node.attributes),
            classes: this.cloneVersionedMap(node.classes),
            customStyles: this.cloneVersionedMap(node.customStyles),
            children: node.children.map(child => this.deepCloneNode(child)),
            createdUserId: node.createdUserId,
            createdAt: node.createdAt,
            updatedUserId: node.updatedUserId,
            updatedAt: node.updatedAt,
        };
    }

    private cloneVersionedMap<K, V>(map: Map<K, VersionedValue<V>>): Map<K, VersionedValue<V>> {
        const cloned = new Map<K, VersionedValue<V>>();
        for (const [key, value] of map.entries()) {
            cloned.set(key, { ...value });
        }
        return cloned;
    }
}
